import { Icon, Flex, Button, Heading, Link as RadixLink } from '@scale/scaleui-radix';
import { faArrowUpRightFromSquare, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useLocation, useHistory, Link as RouterLink } from 'react-router-dom';
import { useMemo } from 'react';
import {
  WebinarStartFilterSchema,
  GetCollectionsSearchParams,
  WebinarStartFilter,
} from '@scale/shared/webinarSessions/types';
import { PaginationState } from '@tanstack/react-table';
import WebinarCollectionsTable from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarCollectionsTable';
import WebinarCollectionsHeader from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarCollectionsHeader';
import path from 'path';

type Props = {
  projectId?: string;
};

const useUrlParams = (
  projectId?: string,
): {
  params: GetCollectionsSearchParams;
  setParams: (newParams: Partial<GetCollectionsSearchParams>) => void;
  pagination: PaginationState;
  setPagination: (newPagination: PaginationState) => void;
} => {
  const location = useLocation();
  const history = useHistory();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const page = parseInt(urlParams.get('page') || '1');
  const pageSize = parseInt(urlParams.get('pageSize') || '10') || 10;

  const pageIndex = isNaN(page) ? 0 : page - 1;

  const search = urlParams.get('search') || '';
  const parsedFilter = WebinarStartFilterSchema.safeParse(urlParams.get('startFilter'));
  const startFilter = parsedFilter.success ? parsedFilter.data : WebinarStartFilter.UPCOMING;

  const hostedByCurrentUser = urlParams.get('hostedByCurrentUser') === 'true';

  const pagination = {
    pageIndex,
    pageSize,
  };

  const setPagination = (newPagination: PaginationState) => {
    const newUrlParams = new URLSearchParams(urlParams);

    newUrlParams.set('page', (newPagination.pageIndex + 1).toString());
    newUrlParams.set('pageSize', newPagination.pageSize.toString());

    if (urlParams.toString() !== newUrlParams.toString()) {
      history.push({
        pathname: location.pathname,
        search: newUrlParams.toString(),
      });
    }
  };

  const setParams = (newParams: Partial<GetCollectionsSearchParams>) => {
    const newUrlParams = new URLSearchParams(urlParams);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value == null) {
        newUrlParams.delete(key);
      } else {
        newUrlParams.set(key, value.toString());
      }
    });

    if (urlParams.toString() !== newUrlParams.toString()) {
      history.push({
        pathname: location.pathname,
        search: newUrlParams.toString(),
      });
    }
  };

  return {
    params: {
      page: pageIndex,
      pageSize,
      search,
      startFilter,
      hostedByCurrentUser,
      project: projectId || '',
    },
    setParams,
    pagination,
    setPagination,
  };
};

const GlobalWebinarView: React.FC<Props> = ({ projectId }) => {
  const { params, setParams, pagination, setPagination } = useUrlParams(projectId);

  const location = useLocation();
  const createWebinarsUrl = path.join(location.pathname, 'create');
  return (
    <Flex direction="column" gap="4" p="4" overflow="hidden" width="100%" height="100%">
      <Flex align="center" justify="between">
        <Heading size="5">Webinars</Heading>
        <Flex gap="2">
          <Button variant="outline" color="neutral" asChild>
            <RadixLink
              href="https://scale.atlassian.net/wiki/spaces/OP/pages/936575062/Webinar+Sessions"
              target="_blank"
              rel="noopener noreferrer"
            >
              Documentation
              <Icon icon={faArrowUpRightFromSquare} label="Documentation" />
            </RadixLink>
          </Button>
          <Button variant="solid" color="accent" asChild>
            <RouterLink to={createWebinarsUrl}>
              <Icon icon={faPlus} label="" />
              Create Webinar
            </RouterLink>
          </Button>
        </Flex>
      </Flex>
      <WebinarCollectionsHeader params={params} setParams={setParams} />
      <WebinarCollectionsTable
        pagination={pagination}
        setPagination={setPagination}
        params={params}
      />
    </Flex>
  );
};

export default GlobalWebinarView;
